<template>
  <div>
    <v-autocomplete
      clearable
      outlined
      dense
      item-value="id"
      hide-details
      prepend-inner-icon="mdi-calendar-clock-outline"
      :label="$t('common.date_options.creation_date')"
      :items="creationDateOptions"
      :value="selectedFilters.createdAtOption"
      @change="handleDateChange($event)"
    />
    <v-menu
      v-if="showCustomCreationDate"
      offset-y
      class="mt-4"
      :close-on-content-click="false"
    >
      <template v-slot:activator="{ on }">
        <v-text-field
          :value="dateRangeText"
          v-on="on"
          dense
          readonly
          hide-details
          outlined
          event-color="primary"
          prepend-inner-icon="mdi-calendar-outline"
          class="font-size-14 my-4"
          :label="$t('common.date_options.date_range')"
        />
      </template>

      <v-date-picker
        v-model="customDateValues"
        style="width: 100%;"
        no-title
        range
        reactive
        :value="dateValues"
        :allowed-dates="isCreationDateAllowed"
        @change="onDateRangeChange($event)"
      />
    </v-menu>
  </div>
</template>

<script>
import * as moment from 'moment'
import _ from 'lodash'

import dateMixin from '@/mixins/date'

const DATE_FILTER_OPTIONS = {
  all: 'all',
  lastWeek: 'lastWeek',
  lastMonth: 'lastMonth',
  last3Months: 'last3Months',
  last12Months: 'last12Months',
  custom: 'custom',
}

export default {
  name: 'CreatedDateSelector',

  props: {
    selectedFilters: {
      type: Object,
      required: true,
    },
  },

  mixins: [dateMixin],

  data() {
    return {
      today: moment().format('YYYY-MM-DD'),
      creationDateOptions: [
        {
          id: DATE_FILTER_OPTIONS.all,
          text: this.$t('common.date_periods.all'),
        },
        {
          id: DATE_FILTER_OPTIONS.lastWeek,
          text: this.$t('common.date_periods.last_week'),
        },
        {
          id: DATE_FILTER_OPTIONS.lastMonth,
          text: this.$t('common.date_periods.last_month'),
        },
        {
          id: DATE_FILTER_OPTIONS.last3Months,
          text: this.$t('common.date_periods.last_3_months'),
        },
        {
          id: DATE_FILTER_OPTIONS.last12Months,
          text: this.$t('common.date_periods.last_12_months'),
        },
        {
          id: DATE_FILTER_OPTIONS.custom,
          text: this.$t('common.date_periods.custom'),
        },
      ],
      customDateValues: [],
    }
  },

  computed: {
    showCustomCreationDate() {
      return (
        this.selectedFilters?.createdAtOption === DATE_FILTER_OPTIONS.custom ??
        false
      )
    },

    dateRangeText() {
      return this.getDateRangeText(
        Object.values(this.selectedFilters?.createdAt)
      )
    },

    dateValues() {
      return _.isEmpty(this.selectedFilters?.createdAt)
        ? []
        : Object.values(this.selectedFilters?.createdAt)
    },
  },

  methods: {
    isCreationDateAllowed(date) {
      return date <= this.today
    },

    handleDateChange(dateRange) {
      switch (dateRange) {
        case DATE_FILTER_OPTIONS.all:
          this.formatDateForFilter(DATE_FILTER_OPTIONS.all, [], 'createdAt')
          break
        case DATE_FILTER_OPTIONS.lastWeek:
          this.formatDateForFilter(
            DATE_FILTER_OPTIONS.lastWeek,
            [
              moment()
                .subtract(7, 'day')
                .format('YYYY-MM-DD'),
              this.today,
            ],
            'createdAt'
          )
          break
        case DATE_FILTER_OPTIONS.lastMonth:
          this.formatDateForFilter(
            DATE_FILTER_OPTIONS.lastMonth,
            [
              moment()
                .subtract(1, 'month')
                .format('YYYY-MM-DD'),

              this.today,
            ],
            'createdAt'
          )
          break
        case DATE_FILTER_OPTIONS.last3Months:
          this.formatDateForFilter(
            DATE_FILTER_OPTIONS.last3Months,
            [
              moment()
                .subtract(3, 'month')
                .format('YYYY-MM-DD'),
              this.today,
            ],
            'createdAt'
          )
          break
        case DATE_FILTER_OPTIONS.last12Months:
          this.formatDateForFilter(
            DATE_FILTER_OPTIONS.last12Months,
            [
              moment()
                .subtract(1, 'year')
                .format('YYYY-MM-DD'),
              this.today,
            ],
            'createdAt'
          )
          break
        case DATE_FILTER_OPTIONS.custom:
          this.formatDateForFilter(
            DATE_FILTER_OPTIONS.custom,
            [],
            'createdAtOption'
          )
          break
        default:
          this.formatDateForFilter('', [], 'createdAtOption')
      }
    },

    onDateRangeChange(range = []) {
      const [startDate, endDate] = range
      const startDateTime = moment(startDate).format()
      const endDateTime = moment(endDate)
        .endOf('day')
        .format()

      if (startDateTime > endDateTime)
        this.$emit(
          'change',
          { from: endDateTime, to: startDateTime },
          'createdAt'
        )
      else
        this.$emit(
          'change',
          { from: startDateTime, to: endDateTime },
          'createdAt'
        )
    },

    formatDateForFilter(option, value, key) {
      if (_.isEmpty(value)) {
        this.$emit('change', [], 'createdAt')
        this.$emit('change', option, 'createdAtOption')
        return
      }
      this.$emit(
        'change',
        {
          from: moment(value[0]).format(),
          to: moment(value[1])
            .endOf('day')
            .format(),
        },
        key
      )
      this.$emit('change', option, 'createdAtOption')
    },
  },
}
</script>
